import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import RecipeCard from "@/components/RecipeCard";
import { Clock, Users, ChefHat, Flame, Soup, ArrowLeft } from "lucide-react";

const FALLBACK_IMG = "/recipe-default.jpg";

export default function RecipeDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/recipes/slug/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="container-x py-20 text-[hsl(var(--text-muted))]">Chargement…</div>;
  if (notFound) return (
    <div className="container-x py-20 text-center" data-testid="recipe-not-found">
      <p className="font-serif text-3xl">Recette introuvable</p>
      <Link to="/recettes" className="mt-4 inline-block text-[hsl(var(--brand-primary))] hover:underline">← Retour aux recettes</Link>
    </div>
  );

  const { recipe, related } = data;
  const giClass =
    recipe.gi_level === "bas" ? "badge-gi-bas" : recipe.gi_level === "modere" ? "badge-gi-modere" : "badge-gi-eleve";
  const totalTime = (recipe.prep_time_min || 0) + (recipe.cook_time_min || 0);

  // Build breadcrumb path based on origin
  const isWorldRecipe = recipe.country && recipe.country !== "France";
  const breadcrumbItems = [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://recette-diabete.com/" },
  ];
  if (isWorldRecipe) {
    breadcrumbItems.push(
      { "@type": "ListItem", position: 2, name: "Cuisines du monde", item: "https://recette-diabete.com/cuisines-du-monde" },
      { "@type": "ListItem", position: 3, name: `Cuisine ${recipe.country}`, item: `https://recette-diabete.com/cuisines-du-monde/${recipe.country.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}` },
      { "@type": "ListItem", position: 4, name: recipe.title, item: `https://recette-diabete.com/recettes/${recipe.slug}` },
    );
  } else {
    breadcrumbItems.push(
      { "@type": "ListItem", position: 2, name: "Recettes", item: "https://recette-diabete.com/recettes" },
      { "@type": "ListItem", position: 3, name: recipe.title, item: `https://recette-diabete.com/recettes/${recipe.slug}` },
    );
  }

  // JSON-LD Recipe schema + Breadcrumb
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: breadcrumbItems },
      {
        "@type": "Recipe",
        name: recipe.title,
        description: recipe.description,
        image: recipe.image_url || FALLBACK_IMG,
        recipeCategory: recipe.category,
        recipeCuisine: recipe.country || "Française",
        keywords: (recipe.keywords || []).join(", "),
        recipeYield: `${recipe.serves} portions`,
        prepTime: `PT${recipe.prep_time_min}M`,
        cookTime: `PT${recipe.cook_time_min}M`,
        totalTime: `PT${totalTime}M`,
        nutrition: {
          "@type": "NutritionInformation",
          calories: recipe.nutrition?.calories ? `${recipe.nutrition.calories} kcal` : undefined,
          carbohydrateContent: recipe.nutrition?.glucides ? `${recipe.nutrition.glucides} g` : undefined,
          proteinContent: recipe.nutrition?.proteines ? `${recipe.nutrition.proteines} g` : undefined,
          fatContent: recipe.nutrition?.lipides ? `${recipe.nutrition.lipides} g` : undefined,
          fiberContent: recipe.nutrition?.fibres ? `${recipe.nutrition.fibres} g` : undefined,
        },
        recipeIngredient: (recipe.ingredients || []).map((i) => `${i.quantity} ${i.name}`),
        recipeInstructions: (recipe.steps || []).map((s, idx) => ({
          "@type": "HowToStep",
          position: idx + 1,
          text: s,
        })),
        suitableForDiet: "https://schema.org/DiabeticDiet",
      },
    ],
  };

  return (
    <>
      <Seo
        title={recipe.title}
        description={recipe.description}
        keywords={recipe.keywords}
        image={`/api/recipes/${recipe.slug}/image`}
        path={`/recettes/${recipe.slug}`}
        type="article"
        jsonLd={jsonLd}
      />

      <article data-testid="recipe-article">
        {/* HERO */}
        <header className="container-x pt-10 pb-8" data-testid="recipe-header">
          <Link to="/recettes" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-primary))]">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Retour
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-6 items-start">
            <div className="md:col-span-7">
              <p className="overline mb-4">
                {recipe.category === "entree" ? "Entrée" : recipe.category === "plat" ? "Plat" : recipe.category === "dessert" ? "Dessert" : recipe.category}
                {recipe.country && recipe.country !== "France"
                  ? ` · ${recipe.country}${recipe.region ? `, ${recipe.region}` : ""}`
                  : recipe.region
                  ? ` · ${recipe.region}`
                  : ""}
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="recipe-title">
                {recipe.title}
              </h1>
              {recipe.subtitle && (
                <p className="mt-4 font-serif italic text-2xl text-[hsl(var(--text-secondary))]">{recipe.subtitle}</p>
              )}
              <p className="mt-6 text-lg text-[hsl(var(--text-secondary))] leading-relaxed max-w-2xl">{recipe.introduction}</p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="recipe-badges">
                <span className={giClass} data-testid="recipe-gi-badge">
                  IG {recipe.gi_level} · {recipe.glycemic_index}
                </span>
                {recipe.classic_glycemic_index && (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-[hsl(var(--brand-primary)/0.1)] text-[hsl(var(--brand-primary))]" data-testid="recipe-classic-gain">
                    Classique : IG {recipe.classic_glycemic_index} → −{recipe.classic_glycemic_index - recipe.glycemic_index} pts
                  </span>
                )}
                {(recipe.profiles || []).map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-[hsl(var(--bg-subtle))] text-[hsl(var(--text-primary))] border border-[hsl(var(--border-default))]" data-testid={`recipe-profile-${p}`}>
                    {p === "DT1" ? "Diabète T1" : p === "DT2" ? "Diabète T2" : "Pré-diabète"}
                  </span>
                ))}
              </div>
            </div>
            <div className="md:col-span-5">
              <img
                src={recipe.image_url || FALLBACK_IMG}
                alt={recipe.title}
                className="w-full aspect-[4/5] object-cover rounded-sm"
                data-testid="recipe-hero-img"
              />
            </div>
          </div>
        </header>

        {/* META BAR */}
        <section className="border-y border-[hsl(var(--border-light))] bg-white" data-testid="recipe-meta-bar">
          <div className="container-x py-6 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-0 md:divide-x md:divide-[hsl(var(--border-light))]">
            <Stat icon={Clock} label="Préparation" value={`${recipe.prep_time_min} min`} />
            <Stat icon={Flame} label="Cuisson" value={`${recipe.cook_time_min} min`} />
            <Stat icon={Users} label="Portions" value={`${recipe.serves} pers.`} />
            <Stat icon={ChefHat} label="Difficulté" value={recipe.difficulty} />
            <Stat icon={Soup} label="Glucides" value={recipe.nutrition?.glucides ? `${recipe.nutrition.glucides} g` : "—"} />
          </div>
        </section>

        {/* BODY */}
        <section className="container-x py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* INGREDIENTS */}
          <aside className="md:col-span-4 md:sticky md:top-24 md:self-start" data-testid="recipe-ingredients">
            <p className="overline mb-3">Ingrédients</p>
            <h2 className="font-serif text-3xl mb-6">Pour {recipe.serves} personnes</h2>
            <ul className="space-y-3">
              {(recipe.ingredients || []).map((ing, i) => (
                <li key={i} className="flex justify-between gap-4 border-b border-[hsl(var(--border-light))] pb-2.5">
                  <span className="text-[hsl(var(--text-primary))]">{ing.name}</span>
                  <span className="text-sm text-[hsl(var(--text-muted))] whitespace-nowrap">{ing.quantity}</span>
                </li>
              ))}
            </ul>
          </aside>

          {/* STEPS */}
          <div className="md:col-span-8" data-testid="recipe-steps">
            <p className="overline mb-3">Préparation</p>
            <h2 className="font-serif text-3xl mb-8">Les étapes</h2>
            <ol className="space-y-8">
              {(recipe.steps || []).map((step, i) => (
                <li key={i} className="flex gap-6" data-testid={`recipe-step-${i + 1}`}>
                  <span className="flex-shrink-0 w-10 h-10 rounded-full bg-[hsl(var(--brand-primary))] text-white font-serif text-xl flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-lg leading-relaxed text-[hsl(var(--text-primary))]">{step}</p>
                </li>
              ))}
            </ol>

            {/* TIPS */}
            {recipe.diabetes_tips?.length > 0 && (
              <div className="mt-16 p-8 border-l-2 border-[hsl(var(--brand-primary))] bg-white" data-testid="recipe-tips">
                <p className="overline mb-3">Conseils diabétiques</p>
                <h3 className="font-serif text-2xl mb-4">Pour adapter au mieux</h3>
                <ul className="space-y-3">
                  {recipe.diabetes_tips.map((tip, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-[hsl(var(--brand-primary))] font-serif italic flex-shrink-0">{i + 1}.</span>
                      <span className="text-[hsl(var(--text-secondary))]">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* SUBSTITUTIONS */}
            {recipe.substitutions?.length > 0 && (
              <div className="mt-12 bg-white border border-[hsl(var(--border-light))] rounded-sm overflow-hidden" data-testid="recipe-substitutions">
                <div className="p-6 border-b border-[hsl(var(--border-light))]">
                  <p className="overline mb-2">Substitutions intelligentes</p>
                  <h3 className="font-serif text-2xl">L'ingéniosité IG bas de cette recette</h3>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--bg-subtle))] text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">Classique</th>
                      <th className="text-left px-6 py-3 font-medium">Substitut IG bas</th>
                      <th className="text-right px-6 py-3 font-medium">IG gagné</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.substitutions.map((s, i) => (
                      <tr key={i} className="border-t border-[hsl(var(--border-light))]" data-testid={`recipe-substitution-${i}`}>
                        <td className="px-6 py-4">
                          <div className="font-medium text-[hsl(var(--text-primary))]">{s.original}</div>
                          {s.note && <div className="text-xs text-[hsl(var(--text-muted))] mt-1 italic">{s.note}</div>}
                        </td>
                        <td className="px-6 py-4 text-[hsl(var(--text-primary))]">{s.substitute}</td>
                        <td className="px-6 py-4 text-right">
                          {s.ig_gain ? (
                            <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-[hsl(var(--gi-low)/0.15)] text-[hsl(var(--gi-low))] font-medium">
                              −{s.ig_gain} pts
                            </span>
                          ) : <span className="text-[hsl(var(--text-muted))]">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* NUTRITION */}
            {recipe.nutrition && (
              <div className="mt-12" data-testid="recipe-nutrition">
                <p className="overline mb-3">Nutrition (par portion)</p>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {[
                    { label: "Calories", value: recipe.nutrition.calories, unit: "kcal" },
                    { label: "Glucides", value: recipe.nutrition.glucides, unit: "g" },
                    { label: "Protéines", value: recipe.nutrition.proteines, unit: "g" },
                    { label: "Lipides", value: recipe.nutrition.lipides, unit: "g" },
                    { label: "Fibres", value: recipe.nutrition.fibres, unit: "g" },
                    { label: "Charge glyc.", value: recipe.nutrition.charge_glycemique, unit: "" },
                  ].map((n) => (
                    <div key={n.label} className="border border-[hsl(var(--border-light))] p-4 bg-white">
                      <p className="overline mb-1.5 text-xs">{n.label}</p>
                      <p className="font-serif text-2xl">{n.value ?? "—"}<span className="text-sm text-[hsl(var(--text-muted))] ml-1">{n.value && n.unit ? n.unit : ""}</span></p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AFFILIATE PRODUCTS */}
            {recipe.affiliate_products?.length > 0 && (
              <div className="mt-12" data-testid="recipe-affiliate">
                <p className="overline mb-3">Ingrédients & ustensiles recommandés</p>
                <h3 className="font-serif text-2xl mb-6">Pour réussir cette recette</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recipe.affiliate_products.map((p, i) => (
                    <a
                      key={i}
                      href={p.url}
                      target="_blank"
                      rel="noopener nofollow sponsored"
                      data-testid={`recipe-affiliate-${i}`}
                      className="block border border-[hsl(var(--border-default))] bg-white p-4 hover:border-[hsl(var(--brand-primary))] transition-colors group"
                    >
                      {p.image_url && (
                        <img src={p.image_url} alt={p.name} className="w-full aspect-square object-cover mb-3 bg-[hsl(var(--bg-subtle))]" loading="lazy" />
                      )}
                      <p className="font-medium text-[hsl(var(--text-primary))] group-hover:text-[hsl(var(--brand-primary))] transition-colors">{p.name}</p>
                      {p.description && <p className="text-xs text-[hsl(var(--text-muted))] mt-1 line-clamp-2">{p.description}</p>}
                      <p className="text-xs text-[hsl(var(--brand-primary))] mt-2 uppercase tracking-wider">
                        {p.provider || "Voir le produit"} →
                      </p>
                    </a>
                  ))}
                </div>
                <p className="mt-3 text-xs text-[hsl(var(--text-muted))] italic">
                  Liens affiliés. Nous touchons une petite commission si vous achetez via ces liens, sans surcoût pour vous.
                </p>
              </div>
            )}

            {/* MEDICAL DISCLAIMER */}
            <div className="mt-16 p-6 border border-[hsl(var(--border-default))] bg-[hsl(var(--bg-subtle))]/50 text-xs text-[hsl(var(--text-secondary))] leading-relaxed rounded-sm" data-testid="medical-disclaimer">
              <p className="overline mb-2">Avertissement médical</p>
              <p>
                Les valeurs d'index glycémique et nutritionnelles indiquées sont estimées et peuvent varier
                selon les marques d'ingrédients, la méthode de cuisson et la digestion individuelle. Cette
                recette est proposée à titre informatif et ne remplace pas l'avis d'un médecin ou d'un
                diététicien. Adaptez les portions et l'intégration dans vos repas selon votre traitement
                et vos recommandations personnelles. En cas de doute, consultez votre professionnel de santé.
              </p>
            </div>
          </div>
        </section>

        {/* RELATED */}
        {related?.length > 0 && (
          <section className="container-x py-16 border-t border-[hsl(var(--border-light))]" data-testid="recipe-related">
            <p className="overline mb-3">Vous aimerez aussi</p>
            <h2 className="font-serif text-4xl mb-10">Recettes similaires</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((r) => <RecipeCard key={r.id} recipe={r} />)}
            </div>
          </section>
        )}
      </article>
    </>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="px-0 md:px-6 first:pl-0">
      <div className="flex items-center gap-2 text-[hsl(var(--text-muted))] mb-1">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="overline text-xs">{label}</span>
      </div>
      <p className="font-serif text-xl capitalize">{value}</p>
    </div>
  );
}
